// Formatting locale + money/date helpers.
//
// The UI *language* (en/es/it) selects translation strings; the *formatting
// locale* additionally depends on the org country, so a Spanish-speaking Mexican
// clinic gets `es-MX` number/date/currency formatting while a Spanish clinic in
// Spain gets `es-ES` — both reusing the same `es` translations. Pure module: no
// React, no fetching.

export type UiLanguage = 'en' | 'es' | 'it';

// Per-language fallback locale when the org country is unknown. `es-419` is the
// pan-Latin-American Spanish locale (better default for LATAM than `es-ES`).
const LANGUAGE_FALLBACK_LOCALE: Record<UiLanguage, string> = {
  en: 'en-US',
  es: 'es-419',
  it: 'it-IT',
};

/** Normalize a free-form country value to an ISO 3166-1 alpha-2 upper-case code. */
export function normalizeCountry(country?: unknown): string | null {
  if (typeof country !== 'string') return null;
  const value = country.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(value) ? value : null;
}

/**
 * Resolve a BCP-47 formatting locale from the UI language + org country.
 * Examples: ('es','MX') -> 'es-MX', ('es','ES') -> 'es-ES', ('es', null) -> 'es-419',
 * ('en','US') -> 'en-US'.
 */
export function resolveFormattingLocale(language: UiLanguage, country?: unknown): string {
  const code = normalizeCountry(country);
  if (code) return `${language}-${code}`;
  return LANGUAGE_FALLBACK_LOCALE[language] ?? language;
}

export function formatCurrency(
  value: number | null | undefined,
  options: { currency: string; locale: string },
): string {
  const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat(options.locale, { style: 'currency', currency: options.currency }).format(amount);
  } catch {
    // Invalid currency/locale -> fall back to a plain number so we never throw in the UI.
    return new Intl.NumberFormat(options.locale).format(amount);
  }
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  options: { locale: string } & Intl.DateTimeFormatOptions,
): string {
  if (value == null) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const { locale, ...rest } = options;
  return date.toLocaleString(locale, Object.keys(rest).length ? rest : { dateStyle: 'medium', timeStyle: 'short' });
}
